import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Rocket, Clock, CheckCircle2, XCircle, HelpCircle, ArrowRight, ExternalLink } from 'lucide-react';

const STATUS_META = {
  pending: { icon: Clock, label: 'Pending Review', color: 'text-[#FCD535]', bg: 'bg-[#FCD535]/10' },
  under_review: { icon: Clock, label: 'Under Review', color: 'text-[#3F8EFC]', bg: 'bg-[#3F8EFC]/10' },
  needs_info: { icon: HelpCircle, label: 'Needs More Info', color: 'text-[#F0B90B]', bg: 'bg-[#F0B90B]/15' },
  approved: { icon: CheckCircle2, label: 'Approved', color: 'text-[#0ECB81]', bg: 'bg-[#0ECB81]/10' },
  rejected: { icon: XCircle, label: 'Rejected', color: 'text-[#F6465D]', bg: 'bg-[#F6465D]/10' },
};

export default function MyListings() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = () => api.get('/listings/my').then(r => setRows(r.data.applications || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="my-listings-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter">My Listing Applications</h1>
          <p className="text-[#848E9C] text-sm mt-1">Track the status of your token listing applications on BITGONODE.</p>
        </div>
        <Link to="/list-your-coin">
          <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="my-listings-new-btn">
            <Rocket className="w-4 h-4 mr-1.5" /> New application
          </Button>
        </Link>
      </div>

      {rows.length === 0 ? (
        <div className="border border-[#2B3139] bg-[#181A20] p-12 text-center">
          <Rocket className="w-12 h-12 text-[#848E9C] mx-auto mb-4" />
          <div className="text-white text-lg font-semibold">No applications yet</div>
          <p className="text-[#848E9C] text-sm mt-2">Submit your first token listing application to get started.</p>
          <Link to="/list-your-coin" className="mt-5 inline-block">
            <Button className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="my-listings-empty-cta">
              List your coin <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {rows.map(a => {
            const meta = STATUS_META[a.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div key={a.application_id} className="border border-[#2B3139] bg-[#181A20] p-5 hover:border-[#FCD535]/40 transition-colors" data-testid={`my-listing-row-${a.application_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    {a.logo_url ? (
                      <img src={a.logo_url} alt="" className="w-10 h-10 rounded-sm border border-[#2B3139] object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    ) : (
                      <div className="w-10 h-10 rounded-sm border border-[#2B3139] bg-[#0B0E11] grid place-items-center text-[#FCD535] font-bold text-sm">
                        {a.token_symbol?.[0] || '?'}
                      </div>
                    )}
                    <div>
                      <div className="text-white font-semibold flex items-center gap-2">
                        {a.token_name} <span className="text-[#FCD535] font-mono text-sm">{a.token_symbol}</span>
                      </div>
                      <div className="text-[10px] text-[#848E9C] font-mono mt-0.5">
                        {a.application_id} · <span className="capitalize">{a.category}</span> · submitted {new Date(a.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] uppercase tracking-wider font-mono ${meta.bg} ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <Button onClick={() => setDetail(a)} size="sm"
                      className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm h-8 text-xs"
                      data-testid={`my-listing-view-${a.application_id}`}>
                      View
                    </Button>
                  </div>
                </div>
                {a.admin_notes && a.status !== 'approved' && (
                  <div className={`mt-3 border-l-2 ${meta.color.replace('text-','border-')} bg-[#0B0E11]/60 px-3 py-2 text-xs text-[#B7BDC6] leading-relaxed`}>
                    <span className={`${meta.color} font-mono uppercase tracking-wider text-[9px]`}>Admin note</span>
                    <div className="mt-0.5">{a.admin_notes}</div>
                  </div>
                )}
                {a.status === 'approved' && a.launchpad_draft_id && (
                  <div className="mt-3 bg-[#0ECB81]/5 border-l-2 border-[#0ECB81] px-3 py-2 text-xs text-[#B7BDC6]">
                    <span className="text-[#0ECB81] uppercase tracking-wider text-[9px] font-mono">Launchpad draft</span>
                    <div className="mt-0.5">A launchpad campaign draft has been created: <span className="font-mono text-white">{a.launchpad_draft_id}</span>. Our team will contact you to finalize dates and tokenomics.</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
          {detail && (() => {
            const meta = STATUS_META[detail.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span className="text-[#FCD535] font-mono">{detail.token_symbol}</span> · {detail.token_name}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${meta.bg} ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Description</div>
                    <div className="text-[#B7BDC6] whitespace-pre-line">{detail.description}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Category</div>
                    <div className="text-white capitalize">{detail.category}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[['Total Supply', detail.total_supply],
                      ['Circulating', detail.circulating_supply],
                      ['Initial Price', detail.initial_price_usd ? `$${detail.initial_price_usd}` : '']].map(([label, v]) => (
                      <div key={label} className="bg-[#0B0E11] border border-[#2B3139] p-3">
                        <div className="text-[10px] uppercase text-[#848E9C]">{label}</div>
                        <div className="font-mono text-white mt-0.5">{v || '—'}</div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Links</div>
                    <div className="flex flex-wrap gap-2">
                      {[['Website', detail.website],
                        ['Whitepaper', detail.whitepaper_url],
                        ['Twitter', detail.twitter],
                        ['Telegram', detail.telegram],
                        ['Discord', detail.discord],
                        ['GitHub', detail.github]].filter(([, v]) => v).map(([k, v]) => (
                        <a key={k} href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#FCD535] hover:underline bg-[#0B0E11] border border-[#2B3139] px-2 py-1">
                          {k} <ExternalLink className="w-3 h-3" />
                        </a>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Reason for listing</div>
                    <div className="text-[#B7BDC6] whitespace-pre-line">{detail.reason}</div>
                  </div>
                  {detail.admin_notes && (
                    <div className={`border-l-2 ${meta.color.replace('text-','border-')} bg-[#0B0E11]/60 px-3 py-2`}>
                      <div className={`${meta.color} uppercase tracking-wider text-[9px] font-mono mb-1`}>Admin note</div>
                      <div className="text-[#B7BDC6] text-xs whitespace-pre-line">{detail.admin_notes}</div>
                    </div>
                  )}
                  <div className="text-[10px] text-[#848E9C] font-mono pt-2 border-t border-[#2B3139]">
                    ID: {detail.application_id} · Submitted {new Date(detail.created_at).toLocaleString()}
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
